import createClient, { Middleware } from "openapi-fetch";
import { getIdToken } from "@src/auth/utils";
import { throttledForceTokenRefresh } from "@src/userEvents";
import Cookies from "js-cookie";

import {
  components as activityLogApiComponents,
  paths as activityLogApiPaths,
} from "./lib/generated-activity-log-api"; // generated from openapi-typescript

import {
  components as ledgerApiComponents,
  paths as ledgerApiPaths,
} from "./lib/generated-ledger-api"; // generated from openapi-typescript

import {
  components as reporterComponents,
  paths as reporterPaths,
} from "./lib/generated-reporter"; // generated from openapi-typescript

import {
  components as siteComponents,
  paths as sitePaths,
} from "./lib/generated-site"; // generated from openapi-typescript

import {
  components as tenantsAndSpacesComponents,
  paths as tenantsAndSpacesPaths,
} from "./lib/generated-tenants-and-spaces"; // generated from openapi-typescript

import {
  components as utilitiesComponents,
  paths as utilitiesPaths,
} from "./lib/generated-utilities"; // generated from openapi-typescript

import {
  components as projectsComponents,
  paths as projectsPaths,
} from "./lib/generated-projects"; // generated from openapi-typescript

import {
  components as d3Components,
  paths as d3Paths,
} from "./lib/generated-d3"; // generated from openapi-typescript

import {
  components as equipmentComponents,
  paths as equipmentPaths,
} from "./lib/generated-equipment"; // generated from openapi-typescript

import {
  components as vendorsComponents,
  paths as vendorsPaths,
} from "./lib/generated-vendors"; // generated from openapi-typescript

import {
  components as certificationsComponents,
  paths as certificationsPaths,
} from "./lib/generated-certifications"; // generated from openapi-typescript

import {
  components as arcadiaComponents,
  paths as arcadiaPaths,
} from "./lib/generated-arcadia"; // generated from openapi-typescript

import {
  components as marketplaceComponents,
  paths as marketplacePaths,
} from "./lib/generated-marketplace"; // generated from openapi-typescript

import {
  components as authComponents,
  paths as authPaths,
} from "./lib/generated-auth"; // generated from openapi-typescript

import {
  components as emailComponents,
  paths as emailPaths,
} from "./lib/generated-email"; // generated from openapi-typescript

import {
  components as rewardsComponents,
  paths as rewardsPaths,
} from "./lib/generated-rewards"; // generated from openapi-typescript

import {
  components as siteAccountsComponents,
  paths as siteAccountsPaths,
} from "./lib/generated-site-accounts"; // generated from openapi-typescript

import {
  components as rebatesComponents,
  paths as rebatesPaths,
} from "./lib/generated-rebates"; // generated from openapi-typescript

import {
  components as unitsComponents,
  paths as unitsPaths,
} from "./lib/generated-units"; // generated from openapi-typescript

import {
  components as nominatorsComponents,
  paths as nominatorsPaths,
} from "./lib/generated-nominators"; // generated from openapi-typescript

import {
  components as notificationComponents,
  paths as notificationPaths,
} from "./lib/generated-notification"; // generated from openapi-typescript

import {
  components as accountsComponents,
  paths as accountsPaths,
} from "./lib/generated-accounts"; // generated from openapi-typescript

import {
  components as controlPanelComponents,
  paths as controlPanelPaths,
} from "./lib/generated-control-panel"; // generated from openapi-typescript

import {
  VITE_ACTIVITY_LOGS_BASE_URL,
  VITE_LEDGER_BASE_URL,
  VITE_REPORTER_BASE_URL,
  VITE_SITE_SERVICE_BASE_URL,
  VITE_TENANTS_AND_SPACES_BASE_URL,
  VITE_UTILITIES_BASE_URL,
  VITE_UPLOAD_BASE_URL,
  VITE_MARKETPLACE_BASE_URL,
  VITE_ARCADIA_BASE_URL,
  VITE_AUTH_SERVICE_BASE_URL,
  VITE_EMAIL_SERVICE_BASE_URL,
  VITE_NOTIFICATION_SERVICE_BASE_URL,
  VITE_REWARDS_SERVICE_BASE_URL,
} from "../../../env";

import { requestShouldThrow } from "../../request";

export type ClientPaths = {
  arcadia: arcadiaPaths;
  activityLog: activityLogApiPaths;
  ledger: ledgerApiPaths;
  reporter: reporterPaths;
  site: sitePaths;
  tenantsAndSpaces: tenantsAndSpacesPaths;
  utilities: utilitiesPaths;
  projects: projectsPaths;
  equipment: equipmentPaths;
  marketplace: marketplacePaths;
  auth: authPaths;
  email: emailPaths;
  rewards: rewardsPaths;
  siteAccounts: siteAccountsPaths;
  rebates: rebatesPaths;
  d3: d3Paths;
  units: unitsPaths;
  nominators: nominatorsPaths;
  notification: notificationPaths;
  accounts: accountsPaths;
  controlPanel: controlPanelPaths;
};

export type ClientOutput = {
  arcadia: arcadiaComponents["schemas"];
  activityLog: activityLogApiComponents["schemas"];
  ledger: ledgerApiComponents["schemas"];
  reporter: reporterComponents["schemas"];
  site: siteComponents["schemas"];
  vendors: vendorsComponents["schemas"];
  certifications: certificationsComponents["schemas"];
  tenantsAndSpaces: tenantsAndSpacesComponents["schemas"];
  utilities: utilitiesComponents["schemas"];
  projects: projectsComponents["schemas"];
  d3: d3Components["schemas"];
  equipment: equipmentComponents["schemas"];
  marketplace: marketplaceComponents["schemas"];
  auth: authComponents["schemas"];
  email: emailComponents["schemas"];
  rewards: rewardsComponents["schemas"];
  siteAccounts: siteAccountsComponents["schemas"];
  rebates: rebatesComponents["schemas"];
  units: unitsComponents["schemas"];
  nominators: nominatorsComponents["schemas"];
  notification: notificationComponents["schemas"];
  accounts: accountsComponents["schemas"];
  controlPanel: controlPanelComponents["schemas"];
};

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const idToken = await getIdToken();
    request.headers.set(
      "Authorization",
      `Bearer ${idToken ?? Cookies.get("jwt")}` // PDF service sets JWT cookie in headless browser from bearer token in request
    );
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 403 || response.status === 401) {
      try {
        // clone before reading so openapi-fetch can still parse the body for callers
        const data = await response.clone().json();

        const isAccountNotAuthorized =
          data?.error?.internal_code === "auth/account-not-authorized";

        if (isAccountNotAuthorized) {
          throttledForceTokenRefresh();
        }
      } catch {
        // ignore json parsing errors
      }
    }
    return response;
  },
};

const customFetch: typeof fetch = (...args) => {
  const [, init] = args;
  const errorMessage = requestShouldThrow({
    safe: init?.method === "GET" || !init?.method,
    // prodonly only matters for 3rd parties i.e. energy star
    prodOnly: false,
    isTestSite: false,
  });
  if (errorMessage) throw new Error(errorMessage);
  return globalThis.fetch(...args);
};

const FetchClients = {
  arcadia: createClient<arcadiaPaths>({
    baseUrl: VITE_ARCADIA_BASE_URL + "/arcadia",
    fetch: customFetch,
  }),
  activityLog: createClient<activityLogApiPaths>({
    baseUrl: VITE_ACTIVITY_LOGS_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  ledger: createClient<ledgerApiPaths>({
    baseUrl: VITE_LEDGER_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  reporter: createClient<reporterPaths>({
    baseUrl: VITE_REPORTER_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  site: createClient<sitePaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  tenantsAndSpaces: createClient<tenantsAndSpacesPaths>({
    baseUrl: VITE_TENANTS_AND_SPACES_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  utilities: createClient<utilitiesPaths>({
    baseUrl: VITE_UTILITIES_BASE_URL + "/sites",
    fetch: customFetch,
  }),
  projects: createClient<projectsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/projects",
    fetch: customFetch,
  }),
  d3: createClient<d3Paths>({
    baseUrl: VITE_UPLOAD_BASE_URL,
    fetch: customFetch,
  }),
  vendors: createClient<vendorsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/vendors",
    fetch: customFetch,
  }),
  certifications: createClient<certificationsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/certifications-report",
    fetch: customFetch,
  }),
  equipment: createClient<equipmentPaths>({
    baseUrl: VITE_TENANTS_AND_SPACES_BASE_URL,
    fetch: customFetch,
  }),
  marketplace: createClient<marketplacePaths>({
    baseUrl: VITE_MARKETPLACE_BASE_URL + "/marketplace",
    fetch: customFetch,
  }),
  auth: createClient<authPaths>({
    baseUrl: VITE_AUTH_SERVICE_BASE_URL + "/auth",
    fetch: customFetch,
  }),
  email: createClient<emailPaths>({
    baseUrl: VITE_EMAIL_SERVICE_BASE_URL + "/email",
    fetch: customFetch,
  }),
  rewards: createClient<rewardsPaths>({
    baseUrl: VITE_REWARDS_SERVICE_BASE_URL + "/rewards",
    fetch: customFetch,
  }),
  siteAccounts: createClient<siteAccountsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/site-accounts",
    fetch: customFetch,
  }),
  rebates: createClient<rebatesPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/rebates",
    fetch: customFetch,
  }),
  units: createClient<unitsPaths>({
    baseUrl: VITE_UTILITIES_BASE_URL + "/units",
    fetch: customFetch,
  }),
  nominators: createClient<nominatorsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/nominators",
    fetch: customFetch,
  }),
  notification: createClient<notificationPaths>({
    baseUrl: VITE_NOTIFICATION_SERVICE_BASE_URL + "/notification",
    fetch: customFetch,
  }),
  accounts: createClient<accountsPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/accounts",
    fetch: customFetch,
  }),
  controlPanel: createClient<controlPanelPaths>({
    baseUrl: VITE_SITE_SERVICE_BASE_URL + "/control-panel",
    fetch: customFetch,
  }),
} as const;

Object.values(FetchClients).forEach((client) => client.use(authMiddleware));

export default FetchClients;
